import React from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';
import { TabletButton } from '@campfire/tablet-button';
import { useApiUrl } from '../../../../../global/config/useApiUrl';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';
import {
  EMPTY_SEEK_OPPORTUNITY_SETTINGS,
  getMissingSeekFields,
  SEEK_REGION_LABELS,
  SeekOpportunityFields,
  SeekOpportunitySettings,
  SeekRegion,
} from '../Integrations/Apps/Setups/SeekVolunteer/SeekOpportunityFields';
import { AdminConsoleFunnel } from './AdminConsoleFunnels';

export type SeekConnection = {
  region: SeekRegion;
  userEmail: string | null;
  defaultCategoryId: number | null;
  defaultCommitmentId: number | null;
  defaultEmail: string | null;
  defaultLocations: string[];
  defaultIsOnlineOrRemote: boolean;
};

const toSettings = (connection: SeekConnection): SeekOpportunitySettings => ({
  categoryId: connection.defaultCategoryId ?? null,
  commitmentId: connection.defaultCommitmentId ?? null,
  email: connection.defaultEmail ?? connection.userEmail ?? '',
  locations: connection.defaultLocations ?? [],
  isOnlineOrRemote: connection.defaultIsOnlineOrRemote ?? false,
});

interface Props {
  funnel: AdminConsoleFunnel;
  connections: SeekConnection[];
  onClose: () => void;
  onPublished: (region: SeekRegion) => void;
  onError: (message: string) => void;
}

export function PublishToSeekDialog({ funnel, connections, onClose, onPublished, onError }: Props) {
  const apiUrl = useApiUrl();
  const { theme } = useCampfireTheme();
  const publishFetch = useCampfireFetch({ defer: true });

  const isLoading = connections.length === 0;
  const [isPublishing, setIsPublishing] = React.useState(false);
  const [region, setRegion] = React.useState<SeekRegion>(connections[0]?.region ?? 'au');
  const [settings, setSettings] = React.useState<SeekOpportunitySettings>(() =>
    connections[0] ? toSettings(connections[0]) : EMPTY_SEEK_OPPORTUNITY_SETTINGS
  );

  const onRegionChange = (nextRegion: SeekRegion) => {
    const connection = connections.find((c) => c.region === nextRegion);
    setRegion(nextRegion);
    if (connection) setSettings(toSettings(connection));
  };

  const missing = getMissingSeekFields(settings);

  const onPublish = () => {
    setIsPublishing(true);
    publishFetch
      .run({
        url: `${apiUrl}/wm/integrations/seek-volunteer/publish-funnel`,
        method: 'post',
        data: {
          region,
          funnelId: funnel.funnelId,
          funnelName: funnel.name,
          funnelDescription: funnel.description || '',
          funnelSlug: funnel.slug,
          categoryId: settings.categoryId,
          commitmentId: settings.commitmentId,
          email: settings.email.trim(),
          locations: settings.locations,
          isOnlineOrRemote: settings.isOnlineOrRemote,
        },
      })
      .then(() => onPublished(region))
      .catch((error: any) => onError(error?.response?.data?.message || 'Failed to publish to SEEK Volunteer'))
      .finally(() => setIsPublishing(false));
  };

  return (
    <Dialog open onClose={isPublishing ? undefined : onClose} fullWidth maxWidth='sm'>
      <DialogTitle>Publish to SEEK Volunteer</DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box display='flex' justifyContent='center' py={4}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Box>
            <Typography variant='body2' style={{ color: '#808080', marginBottom: '1rem' }}>
              {`"${funnel.name}" will be published as an opportunity on SEEK Volunteer ${SEEK_REGION_LABELS[region]}. Values are pre-filled from your defaults for that region.`}
            </Typography>
            {connections.length > 1 && (
              <TextField
                select
                label='Region'
                variant='outlined'
                size='small'
                value={region}
                onChange={(e) => onRegionChange(e.target.value as SeekRegion)}
                fullWidth
                style={{ marginBottom: '1rem' }}
              >
                {connections.map((connection) => (
                  <MenuItem key={connection.region} value={connection.region}>
                    {SEEK_REGION_LABELS[connection.region]}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <SeekOpportunityFields key={region} region={region} value={settings} onChange={setSettings} />
            {missing.length > 0 && (
              <Typography style={{ fontSize: '13px', color: theme.alert.red.light, marginTop: '0.75rem' }}>
                {`Required: ${missing.join(', ')}`}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <TabletButton onClick={onClose} disabled={isPublishing}>
          Cancel
        </TabletButton>
        <TabletButton
          onClick={onPublish}
          variant='contained'
          disabled={isLoading || isPublishing || missing.length > 0}
          style={{
            backgroundColor: theme.color.secondary.main900,
            color: '#ffffff',
            borderRadius: '4px',
            padding: '7px 12px',
            fontWeight: 600,
            fontSize: '13px',
            opacity: isLoading || isPublishing || missing.length > 0 ? 0.5 : 1,
          }}
        >
          {isPublishing ? <CircularProgress size={16} style={{ color: '#fff' }} /> : 'Publish'}
        </TabletButton>
      </DialogActions>
    </Dialog>
  );
}
